import "./SalaryEvolution.scss";
import Icon from "core/icon/Icon";
import InfoContext from "pages/InfoContext";
import InputSelect from "core/inputs/input-select/InputSelect";
import InputText from "core/inputs/input-text/InputText";
import _ from "lodash";
import moment from "moment";
import {
  ACTIONS,
  DATATRACK_MODELS,
  QUERY_STATUS,
  ROLE_MATCH_MODE,
  SCOPES,
  TAG_TYPES,
} from "constants/constants";
import { FormattedMessage, useIntl } from "react-intl";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  LineChart,
  Tooltip,
} from "recharts";
import { Typography } from "core/typography/Typography";
import { formatNames } from "components/form/CommonInputs";
import { useContext, useEffect, useState } from "react";
import {
  CONSULTANT_FORM_LABELS,
  CONSULTANT_FORM_PLACEHOLDERS,
  FORM_INFO,
} from "constants/forms";
import InputSearchTag from "core/inputs/input-search/InputSearchTag";
import colors from "assets/styles/mixins/colors.scss";
import { phoneSize, smallPhoneSize, tabletSize } from "utils/screenSizes";
import PropTypes from "prop-types";
import { canUserPerformAction } from "access-roles/general-functions";
import {
  ROLE_ADMIN_BY_MODEL,
  ROLE_CREATE_BY_MODEL,
  ROLE_DELETE_BY_MODEL,
} from "access-roles/roles";
import LoaderV2 from "core/loader/LoaderV2";
import { renderCommentValue } from "utils/functions";

const getSalaryPercentageIncrease = ({ lastYearValue, currentYearValue }) => {
  return lastYearValue && currentYearValue
    ? (((lastYearValue - currentYearValue) / currentYearValue) * 100).toFixed(2)
    : "-";
};

const CustomTooltip = ({
  active,
  payload,
  label,
  statsHistorySalaryByPersonId,
  currencyList,
}) => {
  const intl = useIntl();
  if (active && payload?.length) {
    const currentData = statsHistorySalaryByPersonId?.filter(
      (elm) => elm?.year === Number(label)
    )[0];
    const lastYearData = statsHistorySalaryByPersonId?.filter(
      (elm) => elm?.year === Number(label) - 1
    )[0];
    const formattedCurrency = (currency_id) =>
      currencyList?.find((elm) => elm.id === currency_id)?.currency_symbol;
    return (
      <div className="custom-tooltip">
        <p>
          <FormattedMessage id={"year"} defaultMessage={"Année"} /> : {label}
        </p>
        <p>
          <FormattedMessage
            id={"consultant_salary"}
            defaultMessage={"Salaire du consultant"}
          />{" "}
          :{" "}
          <span>
            {currentData?.person_salary
              ? `${_.round(currentData?.person_salary, 2)}${formattedCurrency(
                  currentData?.currency_id
                )} (${getSalaryPercentageIncrease({
                  lastYearValue: currentData?.person_salary,
                  currentYearValue: lastYearData?.person_salary,
                })} % / ${intl.formatMessage({
                  id: "year",
                  defaultMessage: "Année",
                })} ${label - 1})`
              : "-"}
          </span>
        </p>
        <p>
          <FormattedMessage id={"max_salary"} defaultMessage={"Salaire max."} />{" "}
          :{" "}
          <span>
            {currentData?.salary_max
              ? `${_.round(currentData?.salary_max, 2)}${formattedCurrency(
                  currentData?.currency_id
                )} (${getSalaryPercentageIncrease({
                  lastYearValue: currentData?.salary_max,
                  currentYearValue: lastYearData?.salary_max,
                })} % /
                ${intl.formatMessage({
                  id: "year",
                  defaultMessage: "Année",
                })} ${label - 1})`
              : "-"}
          </span>
        </p>
        <p>
          <FormattedMessage id={"min_salary"} defaultMessage={"Salaire min."} />{" "}
          :{" "}
          <span>
            {currentData?.salary_min
              ? `${_.round(currentData?.salary_min, 2)}${formattedCurrency(
                  currentData?.currency_id
                )} (${getSalaryPercentageIncrease({
                  lastYearValue: currentData?.salary_min,
                  currentYearValue: lastYearData?.salary_min,
                })} % / ${intl.formatMessage({
                  id: "year",
                  defaultMessage: "Année",
                })} ${label - 1})`
              : "-"}
          </span>
        </p>
        <p>
          <FormattedMessage
            id={"average_salary"}
            defaultMessage={"Salaire moyen"}
          />{" "}
          :{" "}
          <span>
            {currentData?.salary_avg
              ? `${_.round(currentData?.salary_avg, 2)}${formattedCurrency(
                  currentData?.currency_id
                )} (${getSalaryPercentageIncrease({
                  lastYearValue: currentData?.salary_avg,
                  currentYearValue: lastYearData?.salary_avg,
                })} % / ${intl.formatMessage({
                  id: "year",
                  defaultMessage: "Année",
                })} ${label - 1})`
              : "-"}
          </span>
        </p>
        <p>
          <FormattedMessage
            id={"median_salary"}
            defaultMessage={"Salaire médian"}
          />{" "}
          :{" "}
          <span>
            {currentData?.salary_median
              ? `${_.round(currentData?.salary_median, 2)}${formattedCurrency(
                  currentData?.currency_id
                )} (${getSalaryPercentageIncrease({
                  lastYearValue: currentData?.salary_median,
                  currentYearValue: lastYearData?.salary_median,
                })} % / ${intl.formatMessage({
                  id: "year",
                  defaultMessage: "Année",
                })} ${label - 1})`
              : "-"}
          </span>
        </p>
      </div>
    );
  }

  return null;
};

const SalaryStatsGraph = ({
  result,
  statsHistorySalaryByPersonId,
  median_salary_color,
  average_salary_color,
  maximum_salary_color,
  minimum_salary_color,
  consultant_salary_color,
  formatLegend,
  selectedBranch,
  currencyList,
}) => (
  <>
    <div className="no-data">
      {statsHistorySalaryByPersonId?.every((e) => e.nb_person === 0) && (
        <Typography
          className="text violet"
          type="p"
          label={
            <FormattedMessage
              id="data_not_available"
              defaultMessage="Données non disponibles"
            />
          }
        />
      )}
    </div>
    <div className="salary_evolution_stats">
      <LineChart width={1000} height={400} data={result}>
        <CartesianGrid stroke="#f5f5f5" />
        <XAxis dataKey="date" />
        <YAxis
          type="number"
          domain={[0, "dataMax"]}
          unit={selectedBranch[0]?.currency}
        />
        <Tooltip
          content={
            <CustomTooltip
              currencyList={currencyList}
              statsHistorySalaryByPersonId={statsHistorySalaryByPersonId}
            />
          }
        />
        <Line
          type="monotone"
          strokeWidth={2}
          stroke={median_salary_color}
          dataKey={"medianSalary"}
        />
        <Line
          type="monotone"
          strokeWidth={2}
          stroke={average_salary_color}
          dataKey={"averageSalary"}
        />
        <Line
          type="monotone"
          strokeWidth={2}
          stroke={maximum_salary_color}
          dataKey={"maximumSalary"}
        />
        <Line
          type="monotone"
          strokeWidth={2}
          stroke={minimum_salary_color}
          dataKey={"minimumSalary"}
        />
        <Line
          type="monotone"
          strokeWidth={2}
          stroke={consultant_salary_color}
          dataKey={"consultantSalary"}
          activeDot={{ r: 8 }}
        />
        <Legend
          verticalAlign="middle"
          align="right"
          content={formatLegend()}
          layout="vetical"
        />
        <Line />
      </LineChart>
    </div>
  </>
);

const SalaryEvolution = ({
  setValue,
  watch,
  getStatsHistorySalaryByPersonId,
  statsHistorySalaryByPersonId,
  statsHistorySalaryByPersonIdStatus,
}) => {
  const {
    branches,
    consultantComment,
    getPersonById,
    personById,
    id,
    consultantBySlug,
    currencyList,
    currencyListByBranch,
    currencyListByBranchGetter,
    lang,
    commentAction,
    dashboardComments,
    userRoleList,
    userScope,
  } = useContext(InfoContext);
  const {
    comment_settings,
    comment_settings: { title: comment_label, type: comment_type },
  } = FORM_INFO.comment_dashboard || {};

  const branch_id = watch("branch_id");
  const intl = useIntl();

  const model = DATATRACK_MODELS.DASHBOARD_COMMENTS;

  const median_salary_color = colors.orange;
  const average_salary_color = colors.yellow;
  const maximum_salary_color = colors.studio;
  const minimum_salary_color = colors.pink;
  const consultant_salary_color = colors.greenBlue;
  const csltYearsExp = consultantBySlug?.years_experience;

  const [showStats, setShowStats] = useState(false);
  const [selectedYearExperience, setSelectedYearExperience] =
    useState(csltYearsExp);
  const [selectedBranch, setSelectedBranch] = useState([]);
  const selectedBranchId = selectedBranch[0]?.value;

  useEffect(() => {
    if (
      selectedBranchId &&
      selectedBranchId > 0 &&
      selectedYearExperience &&
      selectedYearExperience > 0 &&
      statsHistorySalaryByPersonIdStatus !== QUERY_STATUS.LOADING
    ) {
      getStatsHistorySalaryByPersonId({
        id: id,
        experience: selectedYearExperience,
        branch_id: selectedBranchId,
      });
      currencyListByBranchGetter(selectedBranchId);
    }
  }, [selectedYearExperience, selectedBranch]);

  const updateSelectedYearExperience = (_, val) => {
    setSelectedYearExperience(val);
  };

  useEffect(() => {
    if (branch_id && branches?.length > 0) {
      setSelectedBranch([
        {
          value: branch_id,
          label: branches?.find((elm) => elm.id === branch_id)?.name,
          currency: currencyListByBranch?.find((elm) => elm.is_default)
            ?.currency_symbol,
        },
      ]);
    }
  }, [branch_id, branches]);

  const yearsExpOption = (csltYearsExp) => {
    const options = [
      csltYearsExp >= 2 && {
        label: `${csltYearsExp - 2} ${
          csltYearsExp - 2 <= 1
            ? intl.formatMessage({
                id: "year-2",
                defaultMessage: "an",
              })
            : intl.formatMessage({
                id: "years-2",
                defaultMessage: "ans",
              })
        } (-2 ${intl.formatMessage({
          id: "years-2",
          defaultMessage: "ans",
        })})`,
        value: csltYearsExp - 2,
      },
      csltYearsExp >= 1 && {
        label: `${csltYearsExp - 1} ${
          csltYearsExp - 1 <= 1
            ? intl.formatMessage({
                id: "year-2",
                defaultMessage: "an",
              })
            : intl.formatMessage({
                id: "years-2",
                defaultMessage: "ans",
              })
        } (-1 ${intl.formatMessage({
          id: "year-2",
          defaultMessage: "an",
        })})`,
        value: csltYearsExp - 1,
      },
      {
        label: `${csltYearsExp} ${
          csltYearsExp <= 1
            ? intl.formatMessage({
                id: "year-2",
                defaultMessage: "an",
              })
            : intl.formatMessage({
                id: "years-2",
                defaultMessage: "ans",
              })
        }`,
        value: csltYearsExp,
      },
      {
        label: `${csltYearsExp + 1} ${
          csltYearsExp + 1 <= 1
            ? intl.formatMessage({
                id: "year-2",
                defaultMessage: "an",
              })
            : intl.formatMessage({
                id: "years-2",
                defaultMessage: "ans",
              })
        } (+1 ${intl.formatMessage({
          id: "year-2",
          defaultMessage: "an",
        })})`,
        value: csltYearsExp + 1,
      },
      {
        label: `${csltYearsExp + 2} ${
          csltYearsExp + 2 <= 1
            ? intl.formatMessage({
                id: "year-2",
                defaultMessage: "an",
              })
            : intl.formatMessage({
                id: "years-2",
                defaultMessage: "ans",
              })
        } (+2 ${intl.formatMessage({
          id: "years-2",
          defaultMessage: "ans",
        })})`,
        value: csltYearsExp + 2,
      },
    ];
    return options.filter((option) => option);
  };

  useEffect(() => {
    if (_.has(consultantComment, "user")) {
      getPersonById(consultantComment?.user);
    }
  }, [consultantComment]);

  useEffect(() => {
    if (consultantComment?.comment) {
      setValue("comment", consultantComment.comment || consultantComment);
      setValue("comments", [formatComment(consultantComment)]);
    } else {
      setValue("comment", "");
      setValue("comments", []);
    }
  }, [personById, consultantComment]);

  const formatComment = (val) => {
    if (_.isString(val)) {
      return {
        comment: val,
        date: null,
        user: null,
      };
    } else {
      return {
        ...val,
        user: personById,
        date: val?.date
          ? moment(new Date(val?.date)).format("DD.MM.YYYY")
          : null,
      };
    }
  };

  const result = _.chain(statsHistorySalaryByPersonId)
    .map((elm) => ({
      date: elm?.year,
      medianSalary: elm?.salary_median,
      averageSalary: elm?.salary_avg,
      maximumSalary: elm?.salary_max,
      minimumSalary: elm?.salary_min,
      consultantSalary: elm?.person_salary || 0,
    }))
    .reverse()
    .value();

  const renderLegendContent = (indicatorClass, messageId, defaultMessage) => (
    <div className="content">
      <div className={indicatorClass} />
      {intl.formatMessage({ id: messageId, defaultMessage })}
    </div>
  );

  const formatLegend = () => (
    <div className="legend">
      {renderLegendContent(
        "orange-indicator",
        "median_salary",
        "Salaire médian"
      )}
      {renderLegendContent(
        "yellow-indicator",
        "average_salary",
        "Salaire moyen"
      )}
      {renderLegendContent(
        "violet-indicator",
        "maximum_salary",
        "Salaire maximum"
      )}
      {renderLegendContent(
        "pink-indicator",
        "minimum_salary",
        "Salaire minimum"
      )}
      {renderLegendContent(
        "blue-indicator",
        "consultant_salary",
        "Salaire du consultant"
      )}
    </div>
  );

  const renderSalaryStatsGraph = () =>
    statsHistorySalaryByPersonIdStatus === QUERY_STATUS.LOADING ? (
      <div
        style={{
          width: "100%",
          height: "400px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoaderV2 />
      </div>
    ) : (
      <SalaryStatsGraph
        result={result}
        statsHistorySalaryByPersonId={statsHistorySalaryByPersonId}
        median_salary_color={median_salary_color}
        average_salary_color={average_salary_color}
        maximum_salary_color={maximum_salary_color}
        minimum_salary_color={minimum_salary_color}
        consultant_salary_color={consultant_salary_color}
        formatLegend={formatLegend}
        selectedBranch={selectedBranch}
        currencyList={currencyList}
      />
    );

  return (
    <>
      <div
        style={{ display: "flex", justifyContent: "space-between" }}
        className="section-n"
      >
        <div>
          <Typography
            className="title-3 violet text-uppercase"
            type="h2"
            label={
              <FormattedMessage
                id="salary_evolution_title"
                defaultMessage="Abaque salaire"
              />
            }
          />
          <div className="salary_evolution_section">
            <div className="salary_evolution_cells">
              <div className="cell-padding">
                <InputSelect
                  id="experience"
                  name="years_experience"
                  label={{ id: "experience", defaultMessage: "Expérience" }}
                  options={yearsExpOption(csltYearsExp)}
                  setValue={updateSelectedYearExperience}
                  value={csltYearsExp}
                />
              </div>
              <div className="cell-padding">
                <InputSearchTag
                  label={CONSULTANT_FORM_LABELS.subsidiary}
                  limitChoice={FORM_INFO.branch.limitChoice}
                  onDelete={() => setSelectedBranch([])}
                  options={formatNames(branches)}
                  placeholder={CONSULTANT_FORM_PLACEHOLDERS.branch_id}
                  popup={FORM_INFO.branch.popup}
                  selectedOptions={selectedBranch || []}
                  setValue={setSelectedBranch}
                  type={TAG_TYPES.BRANCHES}
                />
              </div>
            </div>
            <div className="salary_evolution_buttons">
              <button
                className="link-button button-style"
                onClick={() => setShowStats(!showStats)}
              >
                <Icon color="deep-purple" icon="stats" />
                <Typography
                  className="link-1 violet "
                  type="h2"
                  label={
                    !showStats ? (
                      <FormattedMessage
                        id="load_status"
                        defaultMessage="Charger les stats"
                      />
                    ) : (
                      <FormattedMessage
                        id="close_status"
                        defaultMessage="Fermer les stats"
                      />
                    )
                  }
                />
              </button>
            </div>
          </div>
          {(phoneSize || smallPhoneSize || tabletSize) &&
            showStats &&
            renderSalaryStatsGraph()}
        </div>
        <div>
          <Typography
            className="title-3 violet text-uppercase"
            type="h2"
            label={
              <FormattedMessage id="comments" defaultMessage="Commentaires" />
            }
          />
          <div className="commentary_cell">
            <InputText
              comments={dashboardComments}
              id={comment_type}
              label={comment_label}
              isUnderline={dashboardComments?.length > 0}
              marginLeft={15}
              readOnly={true}
              commentSettings={comment_settings}
              addComment={(val) => {
                val &&
                  commentAction({
                    model,
                    action: ACTIONS.CREATE,
                    content: val,
                  });
              }}
              value={renderCommentValue(dashboardComments, intl)}
              lang={lang}
              commentAction={commentAction}
              isCommentsInput={true}
              canUserCreateComment={
                userScope !== SCOPES.READ &&
                canUserPerformAction({
                  userRoles: userRoleList,
                  requiredRoles: [
                    ROLE_CREATE_BY_MODEL.dashboard_comment,
                    ROLE_ADMIN_BY_MODEL.dashboard_comment,
                  ],
                  mode: ROLE_MATCH_MODE.OR,
                })
              }
              canUserDeleteComment={
                userScope !== SCOPES.READ &&
                canUserPerformAction({
                  userRoles: userRoleList,
                  requiredRoles: [
                    ROLE_DELETE_BY_MODEL.dashboard_comment,
                    ROLE_ADMIN_BY_MODEL.dashboard_comment,
                  ],
                  mode: ROLE_MATCH_MODE.OR,
                })
              }
              model={model}
            />
          </div>
        </div>
      </div>

      {!phoneSize &&
        !smallPhoneSize &&
        !tabletSize &&
        showStats &&
        renderSalaryStatsGraph()}
    </>
  );
};

export default SalaryEvolution;

SalaryEvolution.propTypes = {
  control: PropTypes.object,
  register: PropTypes.func,
  setValue: PropTypes.func,
  watch: PropTypes.func,
  readOnly: PropTypes.bool,
  getStatsHistorySalaryByPersonId: PropTypes.func,
  statsHistorySalaryByPersonId: PropTypes.array,
  statsHistorySalaryByPersonIdStatus: PropTypes.string,
};

CustomTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.object,
  label: PropTypes.number,
  currencyList: PropTypes.object,
  statsHistorySalaryByPersonId: PropTypes.array,
};

SalaryStatsGraph.propTypes = {
  result: PropTypes.object,
  statsHistorySalaryByPersonId: PropTypes.object,
  median_salary_color: PropTypes.string,
  average_salary_color: PropTypes.string,
  maximum_salary_color: PropTypes.string,
  minimum_salary_color: PropTypes.string,
  consultant_salary_color: PropTypes.string,
  formatLegend: PropTypes.func,
  selectedBranch: PropTypes.object,
  currencyList: PropTypes.object,
};
